import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Garage cleanout: boxes, old shelving and yard debris, about half a 15-yard truck', amount: '$325' },
  { item: 'Refrigerator, freon recovery and disposal fee included', amount: '$75' },
  { item: 'Two queen mattresses and box springs (recycling surcharge)', amount: '$90' },
  { item: 'Optional: sweep the garage floor once it is empty', amount: '$40' },
];

const faq = [
  {
    q: 'What if there is more junk on the day than in the photos?',
    a: 'Load what the quote covers, then stop and price the rest. In Scopie that means a new request for the same client with its own quote, which they sign before the extra goes on the truck. No haggling at the tailgate.',
  },
  {
    q: 'How should I price junk removal: by volume or by item?',
    a: 'Most crews price the bulk by how much of the truck it fills and add item surcharges for things that cost more to get rid of, such as appliances, mattresses, tires and electronics. Say which method you used on the quote so the customer can compare fairly.',
  },
  {
    q: 'Can customers get an instant price or book a pickup online?',
    a: 'No. Scopie has no online booking or instant pricing, and it does not price from photos. You set the price; Scopie turns it into a quote the customer signs, then a scheduled pickup and an invoice.',
  },
  {
    q: 'Can a property manager forward the quote to the owner?',
    a: 'Yes. The quote is a link with your company name on it, so they can pass it to whoever approves the spend, and that person can sign it.',
  },
];

const page: IndustryPage = {
  slug: 'junk-removal',
  order: 8,
  group: 'Home services',
  path: '/for/junk-removal',
  primaryKeyword: 'junk removal software',
  secondaryKeywords: ['junk removal quote template', 'how to price junk removal', 'junk removal estimating software'],
  title: 'Junk Removal Software: Load-Based Quotes | Scopie', // 49
  metaDescription:
    'Quote junk removal by truck load and item surcharge, agree the rule for extra volume up front, and have the customer sign before the truck rolls out.', // 150
  h1: 'Junk removal quotes, signed before the truck leaves the yard',
  answerFirst:
    'Scopie lets junk removal crews quote by load size with surcharges for mattresses, appliances and tires listed on their own lines, so customers see what drives the price. The rule for extra volume is written in before pickup day, and the customer signs from their phone before you dispatch a truck and two people.',
  exampleQuote: { label: 'Sample garage cleanout quote', lines, total: '$530', depositNote: 'Same-day cancellation terms are the crew\'s own wording.' },
  blocks: [
    {
      type: 'text',
      id: 'photos',
      h2: 'Three companies, the same five photos',
      lead: 'They texted the same pictures to everyone in the search results and booked whoever answered first with a price that made sense.',
      paragraphs: [
        'Junk removal is quoted from photos more than any other trade, which means you are competing on speed and clarity. A reply of "about three hundred" loses to a quote that says half a truck, plus the fridge, plus the mattresses, with the disposal fees spelled out.',
        'Copy the quote link into the same text thread the photos came in. The customer opens it, sees each line, and signs while they are still thinking about it.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'Half a truck and the items that cost extra',
      label: 'Sample quote: garage cleanout',
      lines,
      total: '$530',
      totalLabel: 'Total with the sweep-out',
      note: 'Example prices only. The sweep-out is optional; the customer can tick it on the quote page.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Mistakes that turn a good load into a loss',
      style: 'mistakes',
      items: [
        { title: 'No rule for extra volume', text: 'The pile is always bigger in person. Agree in writing that extra is priced and approved before it is loaded.' },
        { title: 'Surcharge items lumped in', text: 'Appliances with refrigerant, mattresses, tires, paint cans and electronics cost you at the dump. Put each on its own line.' },
        { title: 'Access ignored', text: 'Third-floor walk-ups, long carries and things that must be taken apart double the labor.' },
        { title: 'Hazardous waste not excluded', text: 'Say what you will not haul: chemicals, fuel, asbestos, medical waste.' },
        { title: 'Same-day cancellations absorbed', text: 'A cancelled cleanout costs a truck, a crew and a slot. Your terms should say what happens.' },
        { title: 'Pricing from memory', text: 'Load fractions and surcharges saved as items keep two salespeople from quoting the same garage differently.' },
      ],
    },
    {
      type: 'list',
      id: 'customer-questions',
      h2: 'What customers ask before they sign',
      style: 'checklist',
      items: [
        { text: 'Do I need to be home, or can you take it from the curb or the garage?' },
        { text: 'Does the price include the dump fees?' },
        { text: 'Will you take the old paint and propane tank?' },
        { text: 'What if it fills more of the truck than you thought?' },
        { text: 'Do you donate or recycle anything?' },
      ],
    },
    {
      type: 'text',
      id: 'seasons',
      h2: 'Busy weeks: moves, estates and spring cleaning',
      paragraphs: [
        'Volume spikes around the end of the month when leases turn over, in spring when garages get cleared, and whenever estates are being settled. Realtors and property managers send repeat work in those weeks, and each one stays in your client list with every past quote, so the third cleanout for the same agent takes minutes to price.',
        'Signed pickups go on the schedule with the crew and the truck or trailer assigned. When the load is dumped, share the invoice link from the cab.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Junk removal quote questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/cleaning', anchor: 'Scopie for cleaning businesses', status: 'live' },
    { href: '/for/handyman', anchor: 'Scopie for handyman businesses', status: 'live' },
    { href: '/templates/junk-removal-quote-template', anchor: 'free junk removal quote template', status: 'planned' },
  ],
  cta: {
    heading: 'Answer the photo text with a real quote',
    text: 'Save your load sizes and surcharges in Scopie, and reply to the next request with a quote they can sign.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
